/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  ALERT_TYPE_INFO,
  Alert,
  AlertProps,
  BUTTON_FORMAT_TRANSPARENT,
  Button,
  ButtonList,
  ButtonListProps,
  ICON_DNS,
  ICON_LAUNCH,
  SPINNER_TYPE_REGULAR,
  SdsIcon,
  Spinner,
} from '@squiz/sds';
import {
  ConsoleInstanceType,
  EntityConsoleDatastoreInstance,
  useInstances,
} from 'apps/console-core/src/Services/console';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DXP_EXTERNAL_LINKS } from '../../../../Constants/ExternalLinks.constants';
import { INSTANCE_MANAGEMENT_STRINGS } from '../../../../Constants/Strings.constants';
import { DATASTORE_DATA_IDS } from '../../Datastore.constants';

/**
 * Main Datastore content section.
 */
export const DatastoreContent = (): JSX.Element => {
  const { t } = useTranslation();

  // Labels
  const labels = useMemo(() => {
    return {
      blueprintHeading: t(INSTANCE_MANAGEMENT_STRINGS.blueprints),
      blueprintsEmpty: t(INSTANCE_MANAGEMENT_STRINGS.blueprintsEmpty),
      cliMessage: t(INSTANCE_MANAGEMENT_STRINGS.datastoreCliMessage),
      cliPrompt: t(INSTANCE_MANAGEMENT_STRINGS.datastoreCliPrompt),
      guideHeading: t(INSTANCE_MANAGEMENT_STRINGS.datastoreGuide),
    };
  }, [t]);

  const datastoreCliMessage = <span>{labels.cliMessage}</span>;
  const noBlueprintsMessage = <span>{labels.blueprintsEmpty}</span>;

  const datastoreInstances = useInstances(ConsoleInstanceType.datastore) as Array<EntityConsoleDatastoreInstance>;

  let blueprintInstances: Array<EntityConsoleDatastoreInstance> = [];
  if (datastoreInstances && datastoreInstances.length > 0) {
    blueprintInstances = datastoreInstances.filter((instance: EntityConsoleDatastoreInstance): boolean => {
      return instance.deploymentUrl.startsWith(`https`);
    });
  }

  const getBlueprintList = (): Array<JSX.Element> => {
    return blueprintInstances.map((instance: EntityConsoleDatastoreInstance) => {
      const { deployedAt, deploymentUrl, name, region, secretKey } = instance;
      return (
        <li className="datastore-blueprint" key={deployedAt}>
          <h3 className="datastore-blueprint__name">{name}</h3>
          <span>URL: {deploymentUrl}</span>
          <span>Secret key: {secretKey}</span>
          <span>Region: {region}</span>
        </li>
      );
    });
  };

  const getCliHelp = (): JSX.Element => {
    const findOutButton = (
      <Button
        data-id={DATASTORE_DATA_IDS.buttonCliFindOutMore}
        format={BUTTON_FORMAT_TRANSPARENT}
        icon={ICON_LAUNCH}
        onClick={(): void => {
          window.open(DXP_EXTERNAL_LINKS.datastoreCli, '_blank', 'noopener');
        }}
        text={labels.cliPrompt}
      />
    );

    const buttonListProps: ButtonListProps = {
      buttonKeys: ['cli-find-out-more'],
      buttons: [findOutButton],
    };

    const alertProps: AlertProps = {
      buttonList: <ButtonList {...buttonListProps} />,
      message: datastoreCliMessage,
      type: ALERT_TYPE_INFO,
    };

    return (
      <div className="datastore-help">
        <Alert {...alertProps} />
      </div>
    );
  };

  return (
    <>
      {getCliHelp()}
      <div className="datastore-section">
        <h2 className="datastore-section__heading">
          <SdsIcon svg={ICON_DNS} />
          <span>{labels.blueprintHeading}</span>
        </h2>
        {datastoreInstances ? (
          blueprintInstances.length > 0 ? (
            <ul>{getBlueprintList()}</ul>
          ) : (
            <>{noBlueprintsMessage}</>
          )
        ) : (
          <div className="loading">
            <Spinner type={SPINNER_TYPE_REGULAR} />
          </div>
        )}
      </div>
    </>
  );
};
